import { useState } from "react";
import { AsyncCombobox } from "@/components/shared/async-combobox";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type Product, useProducts } from "@/hooks/use-products";
import { formatCurrency } from "@/lib/format";

interface ProductSelectProps {
  value: string | null;
  onChange: (productId: string, product: Product) => void;
  /** Restrict the searchable set by product status. Defaults to "active". Pass "" for all. */
  status?: string;
  selectedLabel?: string;
  disabled?: boolean;
  className?: string;
}

/** Server-side-searchable single-select for products. */
export function ProductSelect({
  value,
  onChange,
  status = "active",
  selectedLabel,
  disabled,
  className,
}: ProductSelectProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isFetching } = useProducts(
    { search: debouncedSearch, status },
    { cursor: null, direction: "next" },
  );
  const products = data?.data ?? [];

  return (
    <AsyncCombobox
      value={value}
      onChange={onChange}
      options={products}
      getOptionValue={(product) => product.id}
      getOptionText={(product) => product.name}
      getOptionLabel={(product) => (
        <span className="flex w-full items-center justify-between gap-2">
          <span className="flex flex-col">
            <span>{product.name}</span>
            <span className="text-xs text-muted-foreground font-mono">{product.sku}</span>
          </span>
          <span className="text-xs text-muted-foreground">
            {formatCurrency(product.priceCents)}
          </span>
        </span>
      )}
      search={search}
      onSearchChange={setSearch}
      selectedLabel={selectedLabel}
      loading={isFetching}
      disabled={disabled}
      className={className}
      placeholder="Select a product…"
      searchPlaceholder="Search products…"
      emptyText="No products found."
    />
  );
}
